import { useState } from 'react';
import type { ReactElement } from 'react';
import { ChevronRight, File, Folder as FolderIcon } from 'lucide-react';
import { formatDate } from '@xdocs/i18n';
import type { DriveImportItemResult, DriveImportResult, DriveRemoteItem } from '@xdocs/schemas';
import { Badge, Button, Checkbox, EmptyState, Modal, SearchInput, Skeleton, cn, useI18n } from '@xdocs/ui';
import type { BadgeTone } from '@xdocs/ui';
import { JobProgressCard } from '../../../features/jobs/index.js';
import { optionalProp } from '../../../lib/optionalProp.js';
import { useDriveBrowser } from './useDriveBrowser.js';
import { useDriveT } from './useDriveT.js';
import styles from './DrivePickerModal.module.css';

export interface DrivePickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Local explorer folder the import lands in; null = root (F6's
   * `/settings/drive` entry point has no explorer folder context, so this
   * always imports to root — documented scoping decision). */
  targetFolderId: string | null;
}

const OUTCOME_TONE: Record<DriveImportItemResult['outcome'], BadgeTone> = {
  queued: 'success',
  duplicate: 'neutral',
  unsupported_type: 'error',
};

function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`;
}

/** F6 "Drive picker modal (browse, multi-select) → 'Import selected' → one
 * `ingest-worker` job per file". Each queued outcome carries a REAL `jobId`
 * (unlike F18 direct uploads, which have none in the frozen contract) — the
 * existing `<JobProgressCard>` is reused as-is for live ingest progress. */
export function DrivePickerModal({ open, onClose, targetFolderId }: DrivePickerModalProps): ReactElement {
  const { t } = useDriveT();
  const { t: tCommon, locale } = useI18n();
  const browser = useDriveBrowser(open);
  const [importResult, setImportResult] = useState<DriveImportResult | null>(null);

  function handleClose(): void {
    setImportResult(null);
    browser.clearSelection();
    onClose();
  }

  async function handleImport(): Promise<void> {
    const result = await browser.importSelected(targetFolderId);
    setImportResult(result);
  }

  const items = browser.items.filter((item) => !browser.q || item.name.toLowerCase().includes(browser.q.toLowerCase()));

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('driveSettings:picker.title')}
      {...optionalProp('description', importResult ? undefined : t('driveSettings:picker.description'))}
      size="md"
      scrollableBody
      footer={
        importResult ? (
          <div className={cn(styles['footer'])}>
            <Button variant="primary" onClick={handleClose}>
              {t('driveSettings:import.done')}
            </Button>
          </div>
        ) : (
          <div className={cn(styles['footer'])}>
            <span className={cn(styles['selectedCount'])}>
              {browser.selectedIds.size > 0 ? t('driveSettings:picker.selectedCount', { count: browser.selectedIds.size }) : ''}
            </span>
            <Button variant="secondary" onClick={handleClose} disabled={browser.isImporting}>
              {t('driveSettings:picker.cancel')}
            </Button>
            <Button
              variant="primary"
              state={browser.isImporting ? 'loading' : 'idle'}
              disabled={browser.selectedIds.size === 0}
              onClick={() => void handleImport()}
            >
              {t('driveSettings:picker.import')}
            </Button>
          </div>
        )
      }
    >
      {importResult ? (
        <div className={cn(styles['results'])}>
          <p className={cn(styles['resultsTitle'])}>{t('driveSettings:import.resultsTitle')}</p>
          <ul className={cn(styles['resultsList'])}>
            {importResult.items.map((item) => (
              <li key={item.driveFileId} className={cn(styles['resultRow'])}>
                <div className={cn(styles['resultHeader'])}>
                  <span className={cn(styles['resultName'])}>{item.driveFileId}</span>
                  <Badge tone={OUTCOME_TONE[item.outcome]}>{t(`driveSettings:import.outcome.${item.outcome}`)}</Badge>
                </div>
                {item.outcome === 'queued' && item.jobId && <JobProgressCard jobId={item.jobId} className={cn(styles['resultJob'])} />}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <>
          <nav className={cn(styles['breadcrumbs'])} aria-label={t('driveSettings:picker.title')}>
            <button type="button" className={cn(styles['breadcrumbButton'])} onClick={() => browser.navigateToBreadcrumb(-1)}>
              {t('driveSettings:picker.root')}
            </button>
            {browser.breadcrumbs.map((crumb, index) => (
              <span key={crumb.id} className={cn(styles['breadcrumbSegment'])}>
                <span aria-hidden="true">/</span>
                <button type="button" className={cn(styles['breadcrumbButton'])} onClick={() => browser.navigateToBreadcrumb(index)}>
                  {crumb.name}
                </button>
              </span>
            ))}
          </nav>

          <SearchInput
            label={t('driveSettings:picker.searchLabel')}
            hideLabel
            placeholder={t('driveSettings:picker.searchPlaceholder')}
            value={browser.q}
            onChange={(event) => browser.setQ(event.target.value)}
            onClear={() => browser.setQ('')}
            clearLabel={tCommon('common:action.clearFilters')}
            className={cn(styles['search'])}
          />

          {browser.isLoading && <Skeleton shape="row" count={5} />}

          {browser.isError && (
            <EmptyState kind="error" title={t('driveSettings:picker.error.title')} action={{ label: t('driveSettings:action.retry'), onClick: browser.onRetry }} />
          )}

          {!browser.isLoading && !browser.isError && items.length === 0 && <p className={cn(styles['empty'])}>{t('driveSettings:picker.empty')}</p>}

          {!browser.isLoading && !browser.isError && items.length > 0 && (
            <ul className={cn(styles['list'])}>
              {items.map((item: DriveRemoteItem) =>
                item.isFolder ? (
                  <li key={item.id}>
                    <button type="button" className={cn(styles['row'])} onClick={() => browser.navigateInto(item)}>
                      <FolderIcon size={16} aria-hidden="true" />
                      <span className={cn(styles['rowName'])}>{item.name}</span>
                      <ChevronRight size={14} aria-hidden="true" />
                    </button>
                  </li>
                ) : (
                  <li key={item.id} className={cn(styles['fileRow'])}>
                    <Checkbox
                      label={item.name}
                      checked={browser.selectedIds.has(item.id)}
                      onChange={() => browser.toggleSelect(item)}
                    />
                    <File size={16} aria-hidden="true" className={cn(styles['fileIcon'])} />
                    <span className={cn(styles['rowMeta'])}>
                      {formatBytes(item.size)}
                      {item.modifiedAt && ` · ${formatDate(locale, item.modifiedAt, { dateStyle: 'medium' })}`}
                    </span>
                  </li>
                ),
              )}
            </ul>
          )}

          {browser.hasMore && !browser.isLoading && (
            <Button variant="ghost" size="sm" state={browser.isLoadingMore ? 'loading' : 'idle'} onClick={browser.loadMore}>
              {t('driveSettings:picker.loadMore')}
            </Button>
          )}
        </>
      )}
    </Modal>
  );
}
